import { Container, IInit } from '../../../common/container/Container'
import { Task } from '../models/Task'
import { CalendarContainerConfig } from '../container'
import { Observable, of } from 'rxjs'
import { Query } from '../../../common/api/Query'
import { HTTP_CLIENT_KEY, IHTTPClient } from '../../../common/api/HTTPClient'
import { IStatusService } from '../../../common/status/StatusService'
import { catchError, map } from 'rxjs/operators'
import { TaskDTO, toModel } from '../models/TaskDTO'
import { emptyList, ItemList } from '../../../common/models/ItemList'
import { prepareURL } from '../../../common/api/http-helpers'
import { STATUS_SERVICE_KEY } from '../../../container/app'

export interface ITaskApi extends IInit {
  getByID(id: string): Observable<Task | undefined>

  getFilteredList(q: Query<Task>, userId: string): Observable<ItemList<Task>>

  add(e: TaskDTO): Observable<Task | undefined>

  update(e: TaskDTO): Observable<Task | undefined>

  delete(id: string): Observable<boolean>
}

export class TaskApi implements ITaskApi {
  private _container!: Container
  private _httpClient!: IHTTPClient
  private _url!: string
  private _statusService!: IStatusService

  init(c: Container) {
    this._container = c
    this._httpClient = this._container.get<IHTTPClient>(HTTP_CLIENT_KEY)
    this._statusService = this._container.get<IStatusService>(STATUS_SERVICE_KEY)
    this._url = (this._container.config as CalendarContainerConfig).moduleFullUrl + '/tasks'
  }

  add(e: TaskDTO): Observable<Task | undefined> {
    return this._httpClient
      .post<Task>({ url: this._url, body: e })
      .pipe(
        map<TaskDTO, Task>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  delete(id: string): Observable<boolean> {
    return this._httpClient.delete({ url: this._url + '/' + id }).pipe(
      catchError((err) => {
        this._statusService.sendStatus({ variant: 'error', error: err })
        return of(false)
      })
    )
  }

  getByID(id: string): Observable<Task | undefined> {
    return this._httpClient
      .get<Task>({ url: `${this._url}/${id}` })
      .pipe(
        map<TaskDTO, Task>((d) => toModel(d)),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }

  getFilteredList(q: Query<Task>, userId: string): Observable<ItemList<Task>> {
    return this._httpClient
      .get<ItemList<Task>>({ url: prepareURL(`${this._url}/${userId}/list`, q) })
      .pipe(
        map<ItemList<TaskDTO>, ItemList<Task>>((dto) => {
          const itemList = emptyList<Task>()
          itemList.count = dto.count
          itemList.items = dto.items.map((d) => toModel(d))
          return itemList
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(emptyList<Task>())
        })
      )
  }

  update(e: TaskDTO): Observable<Task | undefined> {
    return this._httpClient
      .put<Task>({ url: this._url, body: e })
      .pipe(
        map<TaskDTO, Task>((d) => {
          this._statusService.sendStatus({ variant: 'success' })
          return toModel(d)
        }),
        catchError((err) => {
          this._statusService.sendStatus({ variant: 'error', error: err })
          return of(undefined)
        })
      )
  }
}
